import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { statsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Trophy, Medal, Award, TrendingUp, Star } from 'lucide-react';

export const LeaderboardPage = () => {
  const { user } = useAuth();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      const response = await statsAPI.getLeaderboard(50);
      setLeaders(response.data);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-slate-400" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="font-impact text-xl text-slate-400">{rank}</span>;
    }
  };

  const getRankStyle = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
      case 2:
        return 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200';
      case 3:
        return 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200';
      default:
        return 'bg-white border-slate-200';
    }
  };

  const userRank = leaders.findIndex(l => l.id === user?.id) + 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900">
          Leaderboard
        </h1>
        <p className="text-slate-600 mt-2">
          Top contributors in our community
        </p>
      </div>

      {/* Your Position */}
      {userRank > 0 && (
        <Card className="gradient-primary text-white">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                  <span className="font-impact text-3xl">#{userRank}</span>
                </div>
                <div>
                  <p className="text-indigo-100 text-sm">Your Rank</p>
                  <p className="font-heading font-bold text-xl">{user?.name}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-indigo-100 text-sm">Total Points</p>
                <p className="font-impact text-4xl">{user?.points?.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top 3 Podium */}
      {leaders.length >= 3 && (
        <div className="grid grid-cols-3 gap-4 mb-8">
          {/* Second Place */}
          <div className="flex flex-col items-center pt-8">
            <Avatar className="w-16 h-16 border-4 border-slate-300">
              <AvatarFallback className="bg-slate-200 text-slate-700 text-xl font-bold">
                {getInitials(leaders[1]?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="w-12 h-12 -mt-3 rounded-full bg-slate-200 flex items-center justify-center border-4 border-white">
              <Medal className="w-6 h-6 text-slate-500" />
            </div>
            <p className="font-medium text-slate-900 mt-2 text-center">{leaders[1]?.name}</p>
            <p className="font-impact text-xl text-slate-600">{leaders[1]?.points?.toLocaleString()}</p>
          </div>

          {/* First Place */}
          <div className="flex flex-col items-center">
            <div className="relative">
              <Star className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 text-yellow-500 fill-yellow-500" />
              <Avatar className="w-20 h-20 border-4 border-yellow-400">
                <AvatarFallback className="bg-yellow-100 text-yellow-700 text-2xl font-bold">
                  {getInitials(leaders[0]?.name)}
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="w-14 h-14 -mt-4 rounded-full bg-yellow-400 flex items-center justify-center border-4 border-white shadow-lg">
              <Trophy className="w-7 h-7 text-white" />
            </div>
            <p className="font-heading font-bold text-lg text-slate-900 mt-2 text-center">{leaders[0]?.name}</p>
            <p className="font-impact text-2xl text-yellow-600">{leaders[0]?.points?.toLocaleString()}</p>
          </div>

          {/* Third Place */}
          <div className="flex flex-col items-center pt-12">
            <Avatar className="w-14 h-14 border-4 border-amber-400">
              <AvatarFallback className="bg-amber-100 text-amber-700 text-lg font-bold">
                {getInitials(leaders[2]?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="w-10 h-10 -mt-2 rounded-full bg-amber-500 flex items-center justify-center border-4 border-white">
              <Award className="w-5 h-5 text-white" />
            </div>
            <p className="font-medium text-slate-900 mt-2 text-center">{leaders[2]?.name}</p>
            <p className="font-impact text-lg text-amber-600">{leaders[2]?.points?.toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Full Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-500" />
            All Rankings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {leaders.map((leader, index) => {
              const isCurrentUser = leader.id === user?.id;
              
              return (
                <div 
                  key={leader.id}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    isCurrentUser ? 'ring-2 ring-indigo-500 ring-offset-2' : ''
                  } ${getRankStyle(leader.rank)}`}
                  data-testid={`leader-${leader.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center">
                      {getRankIcon(leader.rank)}
                    </div>
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className={`${
                        leader.rank <= 3 ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-600'
                      } font-medium`}>
                        {getInitials(leader.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-slate-900">
                        {leader.name}
                        {isCurrentUser && <Badge className="ml-2 bg-indigo-100 text-indigo-700">You</Badge>}
                      </p>
                      {leader.bio && (
                        <p className="text-sm text-slate-600 line-clamp-1">{leader.bio}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-impact text-2xl ${
                      leader.rank === 1 ? 'text-yellow-600' :
                      leader.rank === 2 ? 'text-slate-500' :
                      leader.rank === 3 ? 'text-amber-600' :
                      'text-indigo-600'
                    }`}>
                      {leader.points?.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">points</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaderboardPage;
